/* eslint-disable @typescript-eslint/no-explicit-any */
import { TrackIndicators } from 'components/calls/tracks/track_indicators'
import { useTopicPeople } from 'hooks/topic_people_hook'
import { AppLocationContext } from 'pages/admin/app_location_provider'
import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { makeGetTopicById } from 'reduxes/slices/topics/selectors'

import { css } from 'design'
import { Stack } from 'primitives/containers'

import { getCurrentTeammateHandle } from '../../../reduxes/slices/current/selectors'
import { useActiveCallContacts } from '../use_active_call_contacts'
import { CallContactSummary } from './call_contact_summary'

export type CallContactsSummaryProps = {
  isVideoActive: boolean
}

// Show a list or digest of participants when video is not active
// Don't show the list when video is active at desktop breakpoints
// Show the list when video is active at mobile breakpoints
export const CallContactsSummary = ({
  isVideoActive,
}: CallContactsSummaryProps) => {
  const { appLocation } = useContext(AppLocationContext)
  const {
    isOnCall,
    onCall = [],
    notOnCall = [],
    allTwilioParticipants,
  } = useActiveCallContacts()

  const getTopic = useMemo(makeGetTopicById, []) as any
  const topic = useSelector((state) => getTopic(state, appLocation.topicId))
  const { participants } = useTopicPeople(topic)

  const currentTeammateHandle = useSelector(getCurrentTeammateHandle)
  const singleParticipant = useMemo(() => {
    if (participants.length > 2) {
      return
    }

    for (const participant of participants) {
      if (participant.handle !== currentTeammateHandle) {
        return participant
      }
    }
  }, [participants, currentTeammateHandle])

  // If the user is on a 1-on-1 call, show a larger "digest" style of participant
  // This is to emulate more of a phone-call style than the list style we'd normally have
  if (singleParticipant) {
    const { handle } = singleParticipant

    // Determine if the user has connected to the room, or not
    const { callParticipant } =
      onCall.find(
        (c: { topicParticipant: any; callParticipant: any }) =>
          c.callParticipant.handle === handle,
      ) || {}

    const { gid_param } = callParticipant || {}
    const twilioParticipant = allTwilioParticipants?.[gid_param]

    const connectedToRoom = isOnCall
      ? !!twilioParticipant
      : callParticipant?.joined_room_at && !callParticipant?.left_room_at

    return (
      <div className={singleParticipantStyles()}>
        <CallContactSummary
          isConnected={connectedToRoom}
          participant={singleParticipant}
          variant={isVideoActive ? 'list' : 'digest'}
        >
          <TrackIndicators participant={twilioParticipant} handle={handle} />
        </CallContactSummary>
      </div>
    )
  }

  return (
    <Stack className={activeCallContainerStyles()}>
      {onCall.map(({ topicParticipant, callParticipant }: any) => {
        const { handle, gid_param } = callParticipant

        const twilioParticipant = allTwilioParticipants?.[gid_param]
        const connectedToRoom = isOnCall
          ? !!twilioParticipant
          : callParticipant?.joined_room_at && !callParticipant?.left_room_at
        return (
          <CallContactSummary
            key={`participant__${handle}`}
            isConnected={!(onCall.length > 1 && !connectedToRoom)}
            participant={topicParticipant}
          >
            <TrackIndicators participant={twilioParticipant} handle={handle} />
          </CallContactSummary>
        )
      })}

      {notOnCall.map(({ topicParticipant }: any) => (
        <CallContactSummary
          key={`participant__${topicParticipant.handle}`}
          isConnected={false}
          participant={topicParticipant}
        />
      ))}
    </Stack>
  )
}

const activeCallContainerStyles = css({
  display: 'flex',
  flex: '1 1 50%',
  flexDirection: 'column',
  padding: '0 0 100px',
  overflowY: 'auto',

  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 0,

    background:
      'linear-gradient(to top, $backgroundSecondary, $backgroundTransparent 25%)',
  },

  '> *': {
    padding: '$3 $5',

    '&:first-child': {
      borderTop: '1px solid $grey50',
    },

    '&:last-child': {
      borderBottom: '1px solid $grey50',
    },

    '&:only-child': {
      borderTop: 'none',
      borderBottom: 'none',
    },
  },

  '> * + *': {
    borderTop: '1px solid $grey50',
  },
})

const singleParticipantStyles = css({
  height: '50%',

  '> *': {
    padding: '$3 $5',

    '&:first-child': {
      borderTop: '1px solid $grey100',
    },
  },
})
